import { SupabaseClient } from "@supabase/supabase-js";
import { logException, SentryFeatureTags } from "@punchup/sentry";
import { Database } from "../../supabase.types";
import type { SupportMediaUploadTarget } from "../../models/support";

/**
 * The private bucket behind support-case screenshots and recordings (declared,
 * private, in the knowledge-base migration). Nothing in it is public: uploads
 * and reads go through short-lived signed URLs minted here with the
 * service-role client.
 */
export const SUPPORT_MEDIA_BUCKET = "support_case_media";

/** How long a signed read URL stays valid — long enough to read a case. */
export const SUPPORT_MEDIA_READ_URL_TTL_SECONDS = 60 * 60;

type Client = SupabaseClient<Database>;

function fail(
  action: string,
  error: unknown,
  extra?: Record<string, unknown>
): never {
  logException(error, {
    tags: { feature: SentryFeatureTags.SupportAssistant, action },
    extra,
  });
  throw error;
}

/** A one-time URL the browser PUTs the file to (the path is minted by the caller). */
export async function createSupportMediaUploadUrl(
  supabase: Client,
  storagePath: string
): Promise<SupportMediaUploadTarget> {
  const { data, error } = await supabase.storage
    .from(SUPPORT_MEDIA_BUCKET)
    .createSignedUploadUrl(storagePath);

  if (error || !data?.signedUrl || !data?.token) {
    fail("createSupportMediaUploadUrl", error ?? new Error("No upload URL"), {
      storagePath,
    });
  }

  return { storagePath, signedUrl: data.signedUrl, token: data.token };
}

/** Signed read URLs keyed by path; paths that could not be signed are absent. */
export async function createSupportMediaReadUrls(
  supabase: Client,
  storagePaths: string[],
  expiresInSeconds: number = SUPPORT_MEDIA_READ_URL_TTL_SECONDS
): Promise<Map<string, string>> {
  if (storagePaths.length === 0) return new Map();

  const { data, error } = await supabase.storage
    .from(SUPPORT_MEDIA_BUCKET)
    .createSignedUrls(storagePaths, expiresInSeconds);

  if (error) fail("createSupportMediaReadUrls", error);

  const urls = new Map<string, string>();
  for (const entry of data ?? []) {
    if (entry.path && entry.signedUrl && !entry.error) {
      urls.set(entry.path, entry.signedUrl);
    }
  }
  return urls;
}

export type StoredSupportMedia = { sizeBytes: number; mimeType: string | null };

/**
 * What the bucket actually holds at `storagePath`, or null if nothing was
 * uploaded there — checked before a case is allowed to reference an upload, so
 * the size and type come from storage rather than from the client's claim.
 */
export async function getStoredSupportMedia(
  supabase: Client,
  storagePath: string
): Promise<StoredSupportMedia | null> {
  const separator = storagePath.lastIndexOf("/");
  const folder = storagePath.slice(0, separator);
  const name = storagePath.slice(separator + 1);

  const { data, error } = await supabase.storage
    .from(SUPPORT_MEDIA_BUCKET)
    .list(folder, { search: name, limit: 1 });

  if (error) fail("getStoredSupportMedia", error, { storagePath });

  const object = (data ?? []).find((entry) => entry.name === name);
  if (!object) return null;

  return {
    sizeBytes: Number(object.metadata?.size ?? 0),
    mimeType: (object.metadata?.mimetype as string | undefined) ?? null,
  };
}

/** Best-effort removal of objects no case references any more. */
export async function removeSupportMedia(
  supabase: Client,
  storagePaths: string[]
): Promise<void> {
  if (storagePaths.length === 0) return;

  const { error } = await supabase.storage
    .from(SUPPORT_MEDIA_BUCKET)
    .remove(storagePaths);

  // An orphaned object is invisible and harmless; a failed cleanup must not
  // fail the edit that already succeeded.
  if (error) {
    logException(error, {
      tags: {
        feature: SentryFeatureTags.SupportAssistant,
        action: "removeSupportMedia",
      },
      extra: { storagePaths },
    });
  }
}
